import React, { useEffect, useState } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
} from "@mui/material";

import { useAppContext } from "app/AppContext";
import FForm, { useFormApi } from "components/form";
import FormContent from "components/form/FormContent";
import FormRow from "components/form/FormRow";
import TextInput from "components/form/input/TextInput";
import DateInput from "components/form/input/DateInput";
import { useEntity, fetchArray } from "entity";
import Perfil from "entity/Perfil";
import useNotification from "notification";
import SelectButtonInput from "components/form/input/SelectButtonInput";
import { useFieldValue } from "components/form/input/NestedField";
import PerfilAutorizado from "components/PerfilAutorizado";
import firebaseUtil from "firebaseUtil";

const SENHA_MINIMA = 6;

function RedefinirSenhaDialog({ open, onClose, targetUid }) {
    const { showSuccess, showError } = useNotification();
    const [novaSenha, setNovaSenha] = useState("");
    const [confirmarSenha, setConfirmarSenha] = useState("");
    const [enviando, setEnviando] = useState(false);

    const fechar = () => {
        if (enviando) return;
        setNovaSenha("");
        setConfirmarSenha("");
        onClose();
    };

    const confirmar = async () => {
        if (novaSenha.length < SENHA_MINIMA) {
            showError(`A senha precisa ter pelo menos ${SENHA_MINIMA} caracteres`);
            return;
        }
        if (novaSenha !== confirmarSenha) {
            showError("As senhas estão diferentes");
            return;
        }

        setEnviando(true);
        try {
            await firebaseUtil.resetarSenhaUsuario(targetUid, novaSenha);
            showSuccess("Senha redefinida com sucesso");
            fechar();
        } catch (error) {
            switch (error.code) {
                case "functions/permission-denied":
                    showError("Você não tem permissão para redefinir senhas");
                    break;
                case "functions/not-found":
                    showError("Usuário não encontrado no Firebase Auth");
                    break;
                case "functions/invalid-argument":
                    showError(error.message);
                    break;
                default:
                    showError("Falha ao redefinir a senha. Tente novamente.");
                    break;
            }
        } finally {
            setEnviando(false);
        }
    };

    return (
        <Dialog open={open} onClose={fechar} fullWidth maxWidth="xs">
            <DialogTitle>Redefinir senha</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    Defina a nova senha para este usuário. Informe-a a ele por um canal seguro (WhatsApp, pessoalmente
                    etc.).
                </DialogContentText>
                <TextField
                    autoFocus
                    fullWidth
                    type="password"
                    label="Nova senha"
                    value={novaSenha}
                    onChange={(e) => setNovaSenha(e.target.value)}
                    sx={{ mb: 2 }}
                />
                <TextField
                    fullWidth
                    type="password"
                    label="Confirmar nova senha"
                    value={confirmarSenha}
                    onChange={(e) => setConfirmarSenha(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.keyCode === 13 && novaSenha && confirmarSenha && !enviando) {
                            confirmar();
                        }
                    }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={confirmar} disabled={!novaSenha || !confirmarSenha || enviando}>
                    {enviando ? <CircularProgress size={20} /> : "Confirmar"}
                </Button>
                <Button onClick={fechar} disabled={enviando}>
                    Cancelar
                </Button>
            </DialogActions>
        </Dialog>
    );
}

const TipoPerfilObs = () => {
    const [tipoPerfil] = useFieldValue("tipoPerfil");
    return (
        <>
            {/* Configurações para Empresa */}
            {tipoPerfil === "empresa" && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom color="secondary">
                            Configurações de Empresa
                        </Typography>

                        <Alert severity="info" sx={{ mb: 2 }}>
                            Perfis do tipo "Empresa" podem cadastrar rodadas de negócios. Configure os limites de uso
                            abaixo. Deixe os campos vazios para uso ilimitado.
                        </Alert>

                        <FormRow>
                            <TextInput
                                name="saldoRodadas"
                                label="Saldo de Rodadas"
                                type="number"
                                helperText="Quantidade de rodadas permitidas (vazio = ilimitado)"
                            />
                            <DateInput
                                name="dataFimContrato"
                                label="Data Fim do Contrato"
                                helperText="Data limite para uso da plataforma (vazio = ilimitado)"
                            />
                        </FormRow>
                        {/* <FormRow> */}
                        <PerfilAutorizado name="perfisAutorizadoPadrao" label="Perfis Autorizados Padrão" />

                        {/* </FormRow> */}

                        <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Como funciona:</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary" component="ul" sx={{ mt: 1, pl: 2 }}>
                                <li>
                                    <strong>Saldo vazio:</strong> Rodadas ilimitadas
                                </li>
                                <li>
                                    <strong>Saldo numérico:</strong> Cada rodada cadastrada consome 1 crédito
                                </li>
                                <li>
                                    <strong>Data vazia:</strong> Uso sem limite de tempo
                                </li>
                                <li>
                                    <strong>Data definida:</strong> Após vencimento, bloqueia cadastro/edição
                                </li>
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Informações para Administrador */}
            {tipoPerfil === "admin" && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Alert severity="success">
                            <Typography variant="body2">
                                <strong>Perfil Administrador:</strong> Tem acesso completo ao sistema, incluindo
                                gerenciamento de usuários e configurações globais.
                            </Typography>
                        </Alert>
                    </CardContent>
                </Card>
            )}

            {/* Informações para Colaborador */}
            {tipoPerfil === "colaborador" && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Alert severity="info">
                            <Typography variant="body2">
                                <strong>Perfil Colaborador:</strong> Pode participar de rodadas de negócios e acessar
                                funcionalidades básicas do sistema.
                            </Typography>
                        </Alert>
                    </CardContent>
                </Card>
            )}
        </>
    );
};
export default function PerfilForm() {
    const { useSetTitle, setActionsView } = useAppContext();

    const [initialValues, setInitialValues] = useState({});
    const [redefinirSenhaOpen, setRedefinirSenhaOpen] = useState(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { showSuccess, showWarning } = useNotification();
    const form = useFormApi();
    const uidUsuario = searchParams.get("uid");
    const entity = useEntity(Perfil, uidUsuario);

    useEffect(() => {
        const actions = [
            {
                uid: "salvarForm",
                text: "Salvar",
                iconName: "Check",
                sequence: "20",
                action: () => {
                    form.submit();
                },
            },
        ];

        if (uidUsuario) {
            actions.push({
                uid: "redefinirSenha",
                text: "Redefinir Senha",
                iconName: "LockReset",
                sequence: "30",
                action: () => {
                    setRedefinirSenhaOpen(true);
                },
            });
        }

        actions.push({
            uid: "sairForm",
            text: "Sair",
            iconName: "Close",
            sequence: "999999",
            action: () => {
                navigate("/consulta-perfil");
            },
        });

        setActionsView(actions);

        return () => {
            setActionsView([]);
        };
    }, [form, navigate, setActionsView, uidUsuario]);

    useSetTitle("Cadastro de Perfil");

    async function handleSubmit(values) {
        try {
            // Validação de email duplicado
            if (values.email) {
                let query = await Perfil.where("email", "==", values.email);
                const perfil = await fetchArray(query.get());

                if (perfil.length && perfil[0].uid !== values.uid) {
                    showWarning("Email já cadastrado");
                    return;
                }
            }

            // Validações específicas para perfil empresa
            if (values.tipoPerfil === "empresa") {
                // Validação de data fim contrato (apenas se foi preenchida)
                if (values.dataFimContrato) {
                    const fimContrato = new Date(values.dataFimContrato);
                    const hoje = new Date();
                    if (fimContrato <= hoje) {
                        showWarning("Data fim do contrato deve ser posterior à data atual");
                        return;
                    }
                }

                // Validação de saldo (apenas se foi preenchido)
                // Campos vazios/null/undefined = ilimitado
                if (values.saldoRodadas !== undefined && values.saldoRodadas !== null && values.saldoRodadas !== "") {
                    const saldo = parseInt(values.saldoRodadas);
                    if (isNaN(saldo) || saldo < 0) {
                        showWarning(
                            "Saldo de rodadas deve ser um número maior ou igual a zero ou vazio para ilimitado",
                        );
                        return;
                    }
                    values.saldoRodadas = saldo; // Garantir que seja um número
                }
                // Se campo estiver vazio, manter como string vazia para indicar ilimitado
                else if (values.saldoRodadas === "") {
                    values.saldoRodadas = ""; // Manter string vazia = ilimitado
                }
            } else {
                // Para perfis que não são empresa, limpar campos específicos
                delete values.saldoRodadas;
                delete values.dataFimContrato;
            }

            // Sincroniza o e-mail no Firebase Authentication antes de salvar no
            // Firestore, para não gravar um e-mail que falhou de ser aplicado no Auth.
            if (uidUsuario && values.email && values.email !== entity.data.email) {
                try {
                    await firebaseUtil.atualizarEmailUsuario(uidUsuario, values.email);
                } catch (error) {
                    if (error.code === "functions/already-exists") {
                        showWarning("Este e-mail já está em uso por outra conta no Authentication");
                    } else {
                        showWarning("Não foi possível atualizar o e-mail no Authentication. Tente novamente.");
                    }
                    return;
                }
            }

            entity.update(values);

            await entity.save();
            showSuccess("Perfil salvo com sucesso!");
        } catch (error) {
            console.error("Erro ao salvar perfil:", error);
            showWarning("Erro ao salvar perfil. Tente novamente.");
        }
    }

    useEffect(() => {
        setInitialValues(entity.data);
    }, [entity.data]);

    // Monitorar mudanças no tipo de perfil
    // useEffect(() => {
    //     const subscription = form.subscribe(
    //         ({ values }) => {
    //             if (values.tipoPerfil !== tipoPerfil) {
    //                 setTipoPerfil(values.tipoPerfil);
    //             }
    //         },
    //         { values: true }
    //     );

    //     return subscription;
    // }, [form, tipoPerfil]);

    return (
        <>
            <FForm formApi={form} onSubmit={handleSubmit} initialValues={initialValues} sx={{ mt: 1 }}>
                <FormContent sx={{ m: 1 }}>
                    {/* Informações Básicas */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom color="primary">
                                Informações Básicas
                            </Typography>

                            <FormRow>
                                <TextInput name="name" label="Nome" required />
                                <TextInput type="email" name="email" label="E-mail" required />
                            </FormRow>

                            <FormRow>
                                <SelectButtonInput
                                    name="tipoPerfil"
                                    label="Tipo de Perfil"
                                    options={["admin", "empresa", "colaborador"]}
                                    optionsToString={["ADMINISTRADOR", "EMPRESA", "COLABORADOR"]}
                                    required
                                />
                            </FormRow>
                        </CardContent>
                    </Card>
                    <TipoPerfilObs />
                </FormContent>
            </FForm>
            {uidUsuario && (
                <RedefinirSenhaDialog
                    open={redefinirSenhaOpen}
                    onClose={() => setRedefinirSenhaOpen(false)}
                    targetUid={uidUsuario}
                />
            )}
        </>
    );
}
